/**
 * Main-thread client for `bot.worker.ts`. Owns the worker instance for
 * the lifetime of a match (one seed, posted once via `init`), and layers
 * a randomized 300-800ms "thinking" delay on top of the (near-instant)
 * worker computation so bot turns feel natural rather than instantaneous.
 */
import type { BotView } from "@joker-holdem/bot";
import type { Action } from "@joker-holdem/core";

const THINK_DELAY_MIN_MS = 300;
const THINK_DELAY_MAX_MS = 800;

interface DecidedMessage {
  type: "decided";
  requestId: number;
  action: Action;
}

export class BotClient {
  private worker: Worker;
  private nextRequestId = 1;
  private pending = new Map<number, (action: Action) => void>();

  constructor(seed: number) {
    this.worker = new Worker(new URL("./bot.worker.ts", import.meta.url), {
      type: "module",
    });
    this.worker.onmessage = (ev: MessageEvent<DecidedMessage>) => {
      const msg = ev.data;
      if (msg.type !== "decided") return;
      const resolve = this.pending.get(msg.requestId);
      if (resolve) {
        this.pending.delete(msg.requestId);
        resolve(msg.action);
      }
    };
    this.worker.postMessage({ type: "init", seed });
  }

  /** Resolves to the bot's chosen action after the worker computes it plus a randomized think-delay. */
  async decide(view: BotView): Promise<Action> {
    const requestId = this.nextRequestId++;
    const workerResult = new Promise<Action>((resolve) => {
      this.pending.set(requestId, resolve);
      this.worker.postMessage({ type: "decide", requestId, view });
    });
    const delayMs =
      THINK_DELAY_MIN_MS + Math.random() * (THINK_DELAY_MAX_MS - THINK_DELAY_MIN_MS);
    const [action] = await Promise.all([
      workerResult,
      new Promise<void>((resolve) => setTimeout(resolve, delayMs)),
    ]);
    return action;
  }

  dispose(): void {
    this.worker.terminate();
    this.pending.clear();
  }
}
